import React, { useState, Fragment, useEffect } from 'react'
import {
  Button,
  Container,
  Grid,
  IconButton,
  makeStyles,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  CardActions,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  Box,
  useMediaQuery,
  Hidden
} from '@material-ui/core'
import { Edit, GitHub, LinkedIn, PhotoCamera } from '@material-ui/icons'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import router, { useRouter } from 'next/router'

import { useSelector } from 'react-redux'
import ReactMarkdown from 'react-markdown'
import moment from 'moment'
import Link from 'next/link'
import { useAuth, useFetch } from '../../components/context/useFetch'
import Layout from '../../components/layout'
import { pictureUrl } from '../../components/common/pictureUrl'
import WorkIcon from '@material-ui/icons/Work'
import SchoolIcon from '@material-ui/icons/School'
import HumanFileSize from '../../components/common/human_file_size'
import { getFileExtensionIcon } from '../../components/common/get_file_extension_icon'
import { UploadProgress } from '../../components/common/upload_progress'
import { useUpdate } from '../../components/reducer'
import CloseIcon from '@material-ui/icons/Close'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import { Carousel } from 'react-responsive-carousel'
import { Trans } from 'react-i18next'
import i18n from 'i18next'
import { useTheme } from '@material-ui/styles'
import { multiWordInitials } from '../../components/utils/multiWordInitials'
import { EnabledPublicPortfolioActions, EnablePublicPortfolio } from '../profile'
import Head from 'next/head'
import allPagesProps from '../../components/common/all_pages_props'

const PorfFolioRoot = () => {
  const router = useRouter()
  const { pid } = router.query

  if (!pid) return null

  return (
    <Layout>
      <PortFolio id={pid} />
    </Layout>
  )
}

export default PorfFolioRoot

const PortFolio = ({ id }) =>
  useFetch(
    ({ fetch }) => ({
      candidate: fetch(`candidates/${id}`)
    }),
    () => <PortfolioContent />
  )

export const PortfolioContent = () => {
  const route = useRouter()
  const auth = useAuth()
  const candidate = useSelector((state) => state.candidate)

  useEffect(() => {
    if (auth.isCandidate() && auth.getToken().profile_id.toString() === candidate.data.id && candidate.data.public_porfolio_enabled && route.query.pid === 'me')
      router.push(`/t/${candidate.data.public_porfolio_slug}`)
  })

  const editEnabled = auth.isCandidate() && auth.getToken().profile_id.toString() === candidate.data.id

  return (
    <div>
      <CoverPicture edit={editEnabled} />
      <ProfilePicture edit={editEnabled} />

      <Container>
        <Biography />

        <Grid container spacing={4}>
          <Grid item sm={6}>
            <Technologies />
          </Grid>

          <Grid item sm={6}>
            <Documents />
          </Grid>

          <Grid item sm={6}>
            <ProfessionalExperience />
          </Grid>

          <Grid item sm={6}>
            <Education />
          </Grid>

          <Grid item sm={12}>
            <Projects />
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

const useCoverPictureStyles = makeStyles((theme) => ({
  cover: {
    position: 'relative',
    width: '100%',
    backgroundPosition: '50% 50%',
    backgroundSize: 'cover',
    aspectRatio: '5 / 2',
    maxHeight: '256px',
    [theme.breakpoints.up('md')]: {
      maxHeight: '450px',
    },
    'form &': {
      maxHeight: '256px',
    }
  },
  button: {
    position: 'absolute',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    backgroundColor: theme.palette.background.secondary,
    textTransform: 'inherit',
    [theme.breakpoints.down('xs')]: {
      minWidth: '20px'
    }
  }
}))

export const CoverPicture = ({ path = ['candidate', 'data'], edit }) => {
  const classes = useCoverPictureStyles()
  const update = useUpdate()
  const auth = useAuth()

  const coverPicture = useSelector((state) => [...path, 'cover_picture_blob'].reduce((stateSection, key) => stateSection[key], state))
  const candidateId = useSelector((state) => [...path, 'id'].reduce((stateSection, key) => stateSection[key], state))

  const coverFallback = () =>
    coverPicture
      ? pictureUrl({ record_id: candidateId, record_type: 'Candidate', name: 'cover_picture', created_at: coverPicture.created_at }, 1900, 400)
      : '/profile/cover-placeholder.png'

  const uploadHandler = () => {
    update([...path, 'cover_picture_blob'], { created_at: new Date().toISOString() })
  }

  return (
    <div className={classes.cover} style={{ backgroundImage: `url('${coverFallback()}')` }}>
      {edit && (
        <UploadProgress type={'image/png,image/jpg,image/jpeg,image/webp'} updateHandler={uploadHandler} path={'/api/profile'} paramName={'profile[cover_picture]'}>
          {(clickHandler) => (
            <Button id="change-cover" variant="contained" className={classes.button} startIcon={<PhotoCamera />} onClick={clickHandler}>
              <Hidden xsDown>
                <Trans i18nKey="profile.candidat.portfolio.cover_picture" />
              </Hidden>
            </Button>
          )}
        </UploadProgress>
      )}
    </div>
  )
}

const useProfilePictureStyles = makeStyles((theme) => ({
  container: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(5)
    }
  },
  wrapper: {
    position: 'relative',
    marginTop: theme.spacing(-10.5),
    width: 168,
    height: 168,
    border: `4px solid ${theme.palette.background.secondary}`,
    borderRadius: '50%',
    '&::before': {
      position: 'absolute',
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
      content: '" "',
      transition: 'background 0.4s',
      background: 'rgba(0,0,0,0)',
      width: 160,
      height: 160,
      borderRadius: '50%',
      zIndex: 2
    },
    '&:hover::before': {
      background: 'rgba(0,0,0,0.4)'
    }
  },
  img: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    fontSize: 48,
    fontWeight: 500,
    letterSpacing: '-0.05em'
  },
  icon: {
    position: 'absolute',
    bottom: theme.spacing(0.5),
    right: theme.spacing(0.5),
    minWidth: 24,
    padding: theme.spacing(1.5),
    borderRadius: '50%',
    zIndex: 3
  },
  circularProgress: {
    position: 'absolute',
    top: 0,
    width: 160,
    height: 160
  },
  progress: {
    position: 'absolute',
    top: 0,
    width: 160,
    lineHeight: '160px',
    color: theme.palette.background.secondary,
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 500
  }
}))

export const ProfilePicture = ({ path = ['candidate', 'data'], edit }) => {
  const [zoomPicture, setZoomPicture] = useState(false)

  const classes = useProfilePictureStyles()

  const candidate = useSelector((state) => path.reduce((stateSection, key) => stateSection[key], state))

  const update = useUpdate()

  const uploadHandler = () => {
    update([...path, 'picture_blob'], { created_at: new Date().toISOString() })
  }

  return (
    <Fragment>
      {zoomPicture && (
        <Dialog open onClose={() => setZoomPicture(false)}>
          <img src={pictureUrl({ record_type: 'Candidate', record_id: candidate.id, name: 'picture', created_at: candidate.picture_blob?.created_at }, 600, 600)} />
        </Dialog>
      )}

      <Container className={classes.container}>
        <div className={classes.wrapper} onClick={() => candidate.picture_blob && setZoomPicture(true)}>
          {candidate.picture_blob && (
            <img
              className={classes.img}
              src={pictureUrl({ record_type: 'Candidate', record_id: candidate.id, name: 'picture', created_at: candidate.picture_blob?.created_at }, 160, 160)}
            />
          )}

          {!candidate.picture_blob && (
            <Avatar className={classes.img}>
              {multiWordInitials(`${[candidate.first_name, candidate.last_name].filter(Boolean).join(' ')}`)}
            </Avatar>
          )}

          {edit && (
            <UploadProgress
              type={'image/png,image/jpg,image/jpeg,image/webp'}
              updateHandler={uploadHandler}
              path={'/api/profile'}
              paramName={'profile[picture]'}
              alternativeProgress={(progress) => (
                <Fragment>
                  <CircularProgress color="primary" variant="determinate" value={progress} className={classes.circularProgress} size={160} />
                  <span className={classes.progress}>{parseInt(progress)}%</span>
                </Fragment>
              )}>
              {(clickHandler) => (
                <Button variant="contained" className={classes.icon} onClick={clickHandler}>
                  <PhotoCamera />
                </Button>
              )}
            </UploadProgress>
          )}
        </div>
      </Container>
    </Fragment>
  )
}

const appendSchemaIsMissing = (url) => {
  if (!url.match(/^https?:\/\//)) {
    url = `https://${url}`
  }

  return url
}

const pathFromUrl = (url) => {
  url = appendSchemaIsMissing(url)

  const virtualElement = document.createElement('a')
  virtualElement.href = url

  return virtualElement.pathname
}

export const Biography = () => {
  const profile = useSelector((state) => state.profile)
  const candidate = useSelector((state) => state.candidate)
  const user = candidate ? (candidate.included.users || {})[candidate.data.user?.id || -1] || candidate.data : profile.user_attributes || {}

  const concernedUser = !candidate ? profile : candidate.data

  const application = useSelector((state) => state.application?.data)

  const auth = useAuth()
  const router = useRouter()

  return (
    <Fragment>
      <Head>
        <title>{concernedUser.first_name || ''} {concernedUser.last_name || ''} — TrustMe</title>
      </Head>
      <Grid container justifyContent="space-between">
        <Grid item>
          <Typography variant={router.pathname.includes('job-offer') ? 'h2' : 'h1'}>
            {concernedUser.first_name || ''} {concernedUser.last_name || ''}
          </Typography>
        </Grid>

        <Grid item>
          {auth.isCandidate() && auth.getToken().profile_id === parseInt(concernedUser.id) && !router.pathname.includes('job-offer') && (
            <Link href="/profile/infos">
              <Button id="candidat-overview-edit" variant="contained" color="primary">
                <Trans i18nKey="profile.candidat.portfolio.edit_portfolio" />
                <span style={{ display: 'inline-block', width: '10px' }} />
                <Edit />
              </Button>
            </Link>
          )}

          {profile && auth.isCandidate() && auth.getToken().profile_id === parseInt(concernedUser.id) && !router.pathname.includes('job-offer') && (
            <Fragment>
              {profile.public_porfolio_enabled ? <EnabledPublicPortfolioActions share={profile.public_portfolio_slug} /> : <EnablePublicPortfolio profile={profile} />}
            </Fragment>
          )}

          {application && (
            <Typography color="textSecondary" align="right">
              {i18n.t('profile.entreprise.annonce_tab.submitted', { time: moment(application.created_at).format('DD MMMM YYYY') })}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Typography variant={router.pathname.includes('job-offer') ? 'h3' : 'h2'}>
        {concernedUser.title || (candidate?.included.jobs && candidate.included.jobs[candidate.data.job.id]?.label) || ''}
      </Typography>

      <Typography variant="body2" paragraph>
        <ReactMarkdown skipHtml linkTarget="_blank">
          {concernedUser.description}
        </ReactMarkdown>
      </Typography>

      <Grid container>
        <Grid item sm={6}>
          <Typography paragraph>
            <a href={`mailto:${user.email}`}>{user.email}</a> — <a href={`tel:${concernedUser.phone}`}>{concernedUser.phone}</a>
          </Typography>
        </Grid>

        {(concernedUser.linkedin || concernedUser.github) && (
          <Grid item sm={6}>
            <Typography paragraph>
              Liens externes :{' '}
              {concernedUser.linkedin && (
                <IconButton>
                  <a href={appendSchemaIsMissing(concernedUser.linkedin)}>
                    <LinkedIn style={{ color: '#0e76a8' }} className="no-scale" />
                  </a>
                </IconButton>
              )}
              {concernedUser.github && (
                <Fragment>
                  <IconButton>
                    <a href={appendSchemaIsMissing(concernedUser.github)}>
                      <GitHub style={{ color: '6e5494' }} className="no-scale" />
                    </a>
                  </IconButton>
                  {pathFromUrl(concernedUser.github)}
                </Fragment>
              )}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Fragment>
  )
}

const SHOW_MORE_LIMIT = 4

const Technologies = () => {
  const candidates_technologies = useSelector((state) =>
    Object.values(state.candidate.included.candidates_technologies || {}).sort(({ degree: degreeA }, { degree: degreeB }) => degreeB - degreeA)
  )
  const technologies = useSelector((state) => state.candidate.included.technologies)

  const [showAll, setShowAll] = useState(false)

  const handleShowAllTechs = () => {
    setShowAll((currentValue) => !currentValue)
  }

  return (
    <Fragment>
      <Typography variant="h3">{i18n.t('profile.candidat.portfolio.tech_label')}</Typography>

      {candidates_technologies.length > 0 ? (
        <Grid container spacing={3}>
          {candidates_technologies.slice(0, showAll ? Infinity : SHOW_MORE_LIMIT).map((technology) => (
            <Grid item xs={6}>
              <Chip
                avatar={
                  <Avatar>
                    <img src={technologies[technology.technology_id].thumb} />
                  </Avatar>
                }
                label={technologies[technology.technology_id].label}
                color="primary"
                size="large"
              />

              <LinearProgress variant="determinate" value={technology.degree} style={{ marginTop: '2%' }} />
            </Grid>
          ))}

          {candidates_technologies.length > SHOW_MORE_LIMIT && (
            <Button variant="outlined" color="primary" fullWidth position="center" onClick={handleShowAllTechs}>
              {showAll ? i18n.t('close') : i18n.t('profile.candidat.portfolio.show_all')}
            </Button>
          )}
        </Grid>
      ) : (
        <Typography color="textSecondary" style={{ fontStyle: 'italic', fontSize: '14px' }}>
          {i18n.t('profile.candidat.portfolio.no_technologies')}
        </Typography>
      )}
    </Fragment>
  )
}

const DOCUMENT_SHOW_MORE_LIMIT = 3

const Documents = () => {
  const documents = useSelector((state) => Object.values(state.candidate.included.documents || {}))

  const [showAll, setShowAll] = useState(false)

  return (
    <Fragment>
      <Typography variant="h3">{i18n.t('profile.candidat.portfolio.document_label')}</Typography>

      {documents.length > 0 ? (
        <Grid container justifyContent="space-evenly" style={{ minHeight: '120px' }}>
          {documents.slice(0, showAll ? Infinity : DOCUMENT_SHOW_MORE_LIMIT).map((document) => (
            <Grid item xs={4}>
              <Document document={document} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography color="textSecondary" style={{ fontStyle: 'italic', fontSize: '14px' }}>
          {i18n.t('profile.candidat.portfolio.no_documents')}
        </Typography>
      )}

      {documents.length > 0 && documents.length > DOCUMENT_SHOW_MORE_LIMIT && (
        <Button variant="outlined" color="primary" fullWidth position="center" onClick={() => setShowAll((currentValue) => !currentValue)}>
          {showAll ? i18n.t('close') : i18n.t('profile.candidat.portfolio.show_all')}
        </Button>
      )}
    </Fragment>
  )
}

const Document = ({ document }) => {
  const documentType = useSelector((state) => state.candidate.included.document_types[document.document_type.id])

  const auth = useAuth()

  const downloadHandler = (e) => {
    e.preventDefault()

    auth.fetch(`profile/documents/${document.hashid}/download`).then(({ url }) => {
      window.open(url, '_blank')
    })
  }

  return (
    <Fragment>
      <Typography align="center">
        <a href="#" onClick={downloadHandler} style={{ textDecoration: 'none' }}>
          {getFileExtensionIcon(document.file_blob?.filename)}

          <br />
          <Typography variant="body1" component="span" style={{ fontSize: '14px', fontWeight: 'bold' }}>
            {documentType.label}
          </Typography>
          <br />
          <Typography variant="body2" component="span" style={{ fontSize: '12px' }}>
            {document.file_blob?.filename} ({HumanFileSize(document.file_blob?.byte_size)})
          </Typography>
        </a>
      </Typography>
    </Fragment>
  )
}

const ProfessionalExperience = () => (
  <Fragment>
    <Typography variant="h3">
      <WorkIcon style={{ verticalAlign: 'middle' }} /> {i18n.t('profile.candidat.portfolio.experience_label')}
    </Typography>

    <Background type="experience" />
  </Fragment>
)

const Education = () => (
  <Fragment>
    <Typography variant="h3">
      <SchoolIcon style={{ verticalAlign: 'middle' }} />
      {i18n.t('profile.candidat.portfolio.education_label')}
    </Typography>

    <Background type="education" />
  </Fragment>
)

const useBackgroundStyles = makeStyles({
  summary: {
    marginTop: '2%',
    backgroundColor: '#E6E6E6',
    '& .ge--MuiAccordionSummary-content': {
      display: 'initial'
    }
  }
})

const Background = ({ type }) => {
  const backgrounds = useSelector((state) =>
    Object.values(state.candidate.included.backgrounds || {})
      .filter(({ background_type }) => type === background_type)
      .sort((backgroundA, backgroundB) => new Date(backgroundB.from) - new Date(backgroundA.from))
  )

  const [showAll, setShowAll] = useState(false)
  const [expanded, setExpanded] = useState('panel1')

  const handleExpand = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false)
  }

  const handleShowAllProjects = () => {
    setShowAll((value) => !value)
  }

  const classes = useBackgroundStyles()

  return (
    <Fragment>
      {backgrounds.length > 0 ? (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12}>
            {backgrounds.slice(0, showAll ? Infinity : SHOW_MORE_LIMIT).map((background) => (
              <Accordion expanded={expanded === `panel${background.id}`} onChange={handleExpand(`panel${background.id}`)}>
                <AccordionSummary expandIcon={Boolean(background.description) && <ExpandMoreIcon />} className={classes.summary}>
                  <div>
                    <Grid container direction="row" justifyContent="space-between" wrap="nowrap" spacing={5}>
                      <Grid item>
                        <Typography variant="h4" alignItems="right" style={{ fontSize: '15px' }}>
                          {background.structure}
                        </Typography>
                      </Grid>

                      <Grid item>
                        <Typography alignItems="left" color="textSecondary" noWrap style={{ fontSize: '13px' }}>
                          {moment(background.from).format('MMM YYYY')}
                          {' - '}
                          {moment(background.to ? background.to : undefined).format('MMM YYYY')}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Typography variant="h5" alignItems="right" style={{ fontSize: '13px' }}>
                      {background.role}
                    </Typography>
                  </div>
                </AccordionSummary>

                {Boolean(background.description) && (
                  <AccordionDetails>
                    <div>
                      <ReactMarkdown skipHtml linkTarget="_blank">
                        {background.description}
                      </ReactMarkdown>
                    </div>
                  </AccordionDetails>
                )}
              </Accordion>
            ))}
          </Grid>

          {backgrounds.length > 0 && backgrounds.length > SHOW_MORE_LIMIT && (
            <Button variant="outlined" color="primary" fullWidth position="center" onClick={handleShowAllProjects}>
              {showAll ? i18n.t('close') : i18n.t('profile.candidat.portfolio.show_all')}
            </Button>
          )}
        </Grid>
      ) : (
        <Typography color="textSecondary" style={{ fontStyle: 'italic', fontSize: '14px' }}>
          {type === 'experience' ? i18n.t('profile.candidat.portfolio.no_experiences') : i18n.t('profile.candidat.portfolio.no_educations')}
        </Typography>
      )}
    </Fragment>
  )
}

const PROJECTS_SHOW_MORE_LIMIT = 3

const useStyles = makeStyles({
  media: {
    height: 230
  },
  description: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 4,
    lineClamp: 4,
    '-webkit-box-orient': 'vertical'
  }
})

const Projects = () => {
  const auth = useAuth()

  const candidateId = useSelector((state) => state.candidate.data.id)
  const portfolioProjects = useSelector((state) => state.candidate.included.projects || {})
  const portfolioProjectPictures = useSelector((state) => state.candidate.included.project_pictures || {})

  const sortedPortfolioProjects = Object.values(portfolioProjects).sort((projectA, projectB) => new Date(projectB.from) - new Date(projectA.from))

  const [showAll, setShowAll] = useState(false)
  const [selectedProject, setSelectedProject] = useState(0)

  const handleShowAllProjects = () => {
    setShowAll((value) => !value)
  }

  const handleShowProjectPictures = (id) => (e) => {
    setSelectedProject(id)
  }

  const classes = useStyles()

  return (
    <Fragment>
      <Typography variant="h3">{i18n.t('profile.candidat.portfolio.portfolio_label')}</Typography>

      <Grid container spacing={3} justify={portfolioProjects.length > 0 ? 'spacing' : 'center'}>
        {Object.keys(portfolioProjects).length > 0 ? (
          sortedPortfolioProjects.slice(0, showAll ? Infinity : PROJECTS_SHOW_MORE_LIMIT).map((project) => (
            <Grid item xs={12} md={4}>
              <Card onClick={handleShowProjectPictures(project.id)}>
                <CardActionArea>
                  <CardMedia
                    className={classes.media}
                    image={
                      project.pictures[0] &&
                      pictureUrl(
                        {
                          record_id: project.pictures[0].id,
                          record_type: 'Project::Picture',
                          name: 'file',
                          created_at: portfolioProjectPictures[project.pictures[0].id].file_blob.created_at
                        },
                        400,
                        300
                      )
                    }
                    title={project.pictures[0] ? portfolioProjectPictures[project.pictures[0].id].file_blob.filename : ''}
                  />
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="h2">
                      {project.label}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" component="p" className={classes.description}>
                      {project.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
                <CardActions>
                  <Button size="small" color="primary" onClick={handleShowProjectPictures(project.id)}>
                    <Trans i18nKey="profile.candidat.portfolio.see" />
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12} md={4}>
            <Box align="center">
              <Typography color="textSecondary" style={{ fontStyle: 'italic', fontSize: '22px', marginBottom: '2%' }}>
                {i18n.t('profile.candidat.portfolio.empty_slate.body1')}
              </Typography>
              {auth.isCandidate() && auth.getToken().profile_id.toString() === candidateId && (
                <Link href="/profile/portfolio">
                  <Button id="overview-add-project" variant="contained" color="primary">
                    <Trans i18nKey="profile.candidat.portfolio.add_project_button" />
                  </Button>
                </Link>
              )}
            </Box>
          </Grid>
        )}

        {Object.keys(portfolioProjects).length > PROJECTS_SHOW_MORE_LIMIT && (
          <Button variant="outlined" color="primary" fullWidth position="center" onClick={handleShowAllProjects}>
            {showAll ? i18n.t('close') : i18n.t('profile.candidat.portfolio.show_all')}
          </Button>
        )}

        {Boolean(selectedProject) && <ProjectComponent project={portfolioProjects[selectedProject]} closeHandler={() => setSelectedProject(null)} />}
      </Grid>
    </Fragment>
  )
}

const ProjectComponent = (props) => {
  const technologiesList = useSelector((state) => state.candidate.included.technologies || {})
  const portfolioProjectPictures = useSelector((state) => Object.values(state.candidate.included.project_pictures || {}))

  const [openCarousel, setOpenCarousel] = useState(false)
  const [selectedImageID, setSelectedImageID] = useState(0)

  const handlePictureClick = (id) => (e) => {
    setOpenCarousel(true)
    setSelectedImageID(id)
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  return (
    <Dialog open fullScreen={isMobile} fullWidth maxWidth="md" onClose={(e) => props.closeHandler(false)}>
      <DialogTitle>
        <Grid container direction="row" justifyContent="space-between" spacing={2} alignItems="center">
          <Grid item>
            <Typography>{props.project.label}</Typography>
          </Grid>
          <Grid item>
            <IconButton size="large" aria-label="close" onClick={(e) => props.closeHandler(false)}>
              <CloseIcon />
            </IconButton>
          </Grid>
          <Grid item xs={12}>
            <Typography alignItems="left" color="textSecondary" noWrap style={{ fontSize: '13px' }}>
              {moment(props.project.from).format('MMM YYYY')}
              {' - '}
              {moment(props.project.to ? props.project.to : undefined).format('MMM YYYY')}
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ReactMarkdown skipHtml linkTarget="_blank">
              {props.project.description}
            </ReactMarkdown>
          </Grid>
          <Grid item xs={12}>
            {props.project.technology_ids.map((technologyId) => (
              <Chip
                avatar={
                  <Avatar>
                    <img style={{ borderRadius: '50%' }} src={pictureUrl({ record_type: 'Technology', record_id: technologyId, name: 'icon' }, 40, 40)} />
                  </Avatar>
                }
                size="medium"
                label={technologiesList[technologyId].label}
              />
            ))}
          </Grid>
          {props.project.pictures.length > 0 &&
            props.project.pictures.map((picture, idx) => (
              <Grid item xs={12} md={6}>
                <img
                  style={{ border: '1px solid #cdcdcd' }}
                  src={pictureUrl(
                    {
                      record_id: picture.id,
                      record_type: 'Project::Picture',
                      name: 'file',
                      created_at: portfolioProjectPictures.find(({ id }) => id === picture.id).file_blob.created_at
                    },
                    340,
                    165
                  )}
                  alt="project_image"
                  onClick={handlePictureClick(idx)}
                />
              </Grid>
            ))}

          {openCarousel && (
            <Dialog
              open={openCarousel}
              onClose={() => setOpenCarousel(false)}
              PaperProps={{
                style: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none'
                }
              }}>
              <Carousel selectedItem={selectedImageID}>
                {props.project.pictures.map((picture) => (
                  <div>
                    <img
                      style={{ border: '1px solid #cdcdcd' }}
                      src={pictureUrl(
                        {
                          record_id: picture.id,
                          record_type: 'Project::Picture',
                          name: 'file',
                          created_at: portfolioProjectPictures.find(({ id }) => id === picture.id).file_blob.created_at
                        },
                        900,
                        900,
                        'Project picture image',
                        'resize_to_limit'
                      )}
                      alt="project_image"
                      onClick={() => setOpenCarousel(true)}
                    />
                  </div>
                ))}
              </Carousel>
            </Dialog>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={(e) => props.closeHandler(false)}>
          <Trans i18nKey="registration.candidat.back" />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const getServerSideProps = async (context) => ({ props: await allPagesProps(context)
})
